import React, { useMemo } from 'react';

import isEmpty from 'lodash/isEmpty';

import { SidePage, Section } from '@gogame/components';
import { useChildComponent } from '@gogame/hooks-ts';

import { CampaignDetails } from 'scenes/Campaigns/GGCampaigns/components/CampaignDetails';
import InfluencerCampaignDetails from 'scenes/Influencers/InfluencersCampaigns/components/InfluencerCampaignDetails';
import MasterCampaignDetails from 'scenes/Influencers/MasterCampaigns/components/MasterCampaignDetails';
import Actions from 'scenes/PromoCodes/components/Actions';
import ConditionalRewardsTable from 'scenes/PromoCodes/components/ConditionalRewardsTable';
import {
  PublicPromoCodeFormEdit,
  PromoCodeSetFormEdit,
  PublicPromoCodeFormDuplicate,
} from 'scenes/PromoCodes/components/PromoCodesForm';
import RedeemTable from 'scenes/PromoCodes/components/RedeemTable';
import ScreenLinkToTracker from 'scenes/PromoCodes/components/ScreenLinkToTracker';
import {
  useLoadPromoCodeDetails,
  useReloadPromoCodeDetails,
} from 'scenes/PromoCodes/data';
import { useIsUpdating } from 'scenes/PromoCodes/data';
import { usePeriodForScreenTracker } from 'scenes/PromoCodes/hooks';

import GeneralSection from './GeneralSection';
import Metadata from './Metadata';
import SectionWithIdNameTable from './SectionWithIdNameTable';
import ReceiveNotificationSwitcher from './ReceiveNotificationSwitcher';

const PromoCodeDetails = ({ id, onClose, onExited, open }) => {
  const isUpdating = useIsUpdating();
  const periodForScreenTracker = usePeriodForScreenTracker();

  const [childComponent, openChildComponent] = useChildComponent();

  const { data, isLoading, isFetching } = useLoadPromoCodeDetails(id);

  const handleChange = useReloadPromoCodeDetails(id);

  const handleDuplicate = () => {
    openChildComponent(PublicPromoCodeFormDuplicate, {
      id: id,
      onSubmit: handleChange,
    });
  };

  const handleEditPublicAction = () => {
    openChildComponent(PublicPromoCodeFormEdit, {
      id,
      onSubmit: handleChange,
    });
  };

  const handleEditSetAction = () => {
    openChildComponent(PromoCodeSetFormEdit, {
      id,
      onSubmit: handleChange,
    });
  };

  const handleOpenInfluencerCampaigns = ({ id }) => {
    openChildComponent(InfluencerCampaignDetails, {
      id: id,
      onChange: handleChange,
    });
  };

  const handleOpenMasterCampaigns = ({ id }) => {
    openChildComponent(MasterCampaignDetails, {
      id: id,
      onChange: handleChange,
    });
  };

  const handleOpenCampaigns = ({ id }) => {
    openChildComponent(CampaignDetails, {
      id: id,
      onChange: handleChange,
    });
  };

  const hasConditionalRewards = useMemo(() => {
    return data?.conditionalRewardsCount || data?.conditionalRewardsBreakdown;
  }, [data]);

  return (
    <SidePage
      backdrop={false}
      onClose={onClose}
      onExited={onExited}
      fetching={isFetching}
      loading={isLoading || isUpdating}
      open={open}
      size={'lg'}
    >
      <SidePage.Title
        title={`Promo Code ${data?.id ? `#${data.id}` : ''}`}
        subTitle={data?.name}
        actions={
          <React.Fragment>
            <ReceiveNotificationSwitcher
              id={data?.id}
              value={data?.receiveNotifications}
            />
          </React.Fragment>
        }
      />
      <SidePage.NewBody wrapper={true} dense={false}>
        <SidePage.NewContent withSidebar={true}>
          {!isEmpty(data) ? (
            <React.Fragment>
              <Section title={'General'}>
                <GeneralSection data={data} />
              </Section>
              {hasConditionalRewards && (
                <Section
                  title={'Conditional Rewards'}
                  subTitle={
                    data?.conditionalRewardsTimer > 0
                      ? `${data?.conditionalRewardsTimer} days for completion`
                      : undefined
                  }
                  isCollapsable
                  initialOpen={false}
                >
                  <ConditionalRewardsTable data={data} />
                </Section>
              )}
              <Section
                title={'Redemption Breakdown'}
                isCollapsable
                initialOpen={false}
              >
                <RedeemTable data={data} />
              </Section>
              <Section title={'GG Campaigns'} isCollapsable initialOpen={false}>
                <SectionWithIdNameTable
                  data={data.goGameCampaigns}
                  onLinkOpen={handleOpenCampaigns}
                  renderId={({ id, name }) => (
                    <ScreenLinkToTracker
                      params={{
                        dimensionSearchQuery: name,
                        filtersCollection: [
                          {
                            period: periodForScreenTracker({
                              startDate: data.activityPeriod[0],
                            }),
                            ...(data.affiliateId && {
                              affiliates: [data.affiliateId],
                            }),
                          },
                        ],
                        drillPath: [{ type: 'Campaign' }],
                      }}
                    >
                      {id}
                    </ScreenLinkToTracker>
                  )}
                />
              </Section>
              <Section
                title={'Influencer Campaigns'}
                isCollapsable
                initialOpen={false}
              >
                <SectionWithIdNameTable
                  data={data.influencerCampaigns}
                  onLinkOpen={handleOpenInfluencerCampaigns}
                />
              </Section>
              <Section
                title={'Master Campaigns'}
                isCollapsable
                initialOpen={false}
              >
                <SectionWithIdNameTable
                  data={data.masterCampaigns}
                  onLinkOpen={handleOpenMasterCampaigns}
                />
              </Section>
            </React.Fragment>
          ) : null}
        </SidePage.NewContent>
        <SidePage.Sidebar
          actions={
            <Actions
              data={data}
              actions={data?.actions}
              onEdit={handleEditPublicAction}
              onEditSet={handleEditSetAction}
              onDuplicate={handleDuplicate}
            />
          }
        >
          <Metadata data={data} />
        </SidePage.Sidebar>
        {childComponent}
      </SidePage.NewBody>
      <SidePage.Actions>
        <SidePage.CloseButton />
      </SidePage.Actions>
    </SidePage>
  );
};

export default PromoCodeDetails;
